/**
 * Counter leggeri per le voci del menu laterale: progetti attivi, preventivi
 * aperti (bozza/inviato), materiali totali. Query lightweight con `head: true`
 * + `count: 'exact'` per non scaricare i record.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';

export interface SidebarCounts {
  activeProjects: number;
  openEstimates: number;
  customers: number;
  pendingFollowUps: number;
}

export const useSidebarCounts = (): SidebarCounts => {
  const { currentOrganizationId } = useCurrentOrganization();

  const { data } = useQuery({
    queryKey: ['sidebar-counts', currentOrganizationId],
    enabled: !!currentOrganizationId,
    // Counter possono cambiare spesso; teniamo cache breve.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<SidebarCounts> => {
      if (!currentOrganizationId) {
        return { activeProjects: 0, openEstimates: 0, customers: 0, pendingFollowUps: 0 };
      }

      const [projRes, estRes, custRes, followUpRes] = await Promise.all([
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from('projects')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', currentOrganizationId)
          .eq('status', 'active'),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from('estimates')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', currentOrganizationId)
          .in('status', ['draft', 'sent', 'pending']),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from('customers')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', currentOrganizationId),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase as any)
          .from('project_activities')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', currentOrganizationId)
          .or(
            'and(contact_at.not.is.null,contact_done_at.is.null),and(follow_up_at.not.is.null,completed_at.is.null)'
          ),
      ]);

      return {
        activeProjects: projRes.count ?? 0,
        openEstimates: estRes.count ?? 0,
        customers: custRes.count ?? 0,
        pendingFollowUps: followUpRes.count ?? 0,
      };
    },
  });

  return data ?? { activeProjects: 0, openEstimates: 0, customers: 0, pendingFollowUps: 0 };
};
